import { IQService } from 'angular';
import { IPromise } from 'q';
import { ObjectId } from '../../../../../..';
import { Form, FormQuestion } from '../../../../../../services/API/forms/forms';
import { Answer } from '../../../../../../services/API/reports/reports';
import { DateFormatService } from '../../../../../../services/Utils/Dates/date-format.service';
import { DateService } from '../../../../../../services/Utils/Dates/date.service';
import { ImageService } from '../../../../../../services/Utils/Image/image.service';
import { ImageSourceService } from '../../../../../../services/Utils/ImageSource/image-source.service';
import { ModalService } from '../../../../../../services/Utils/Modal';
import {
  getProductQuestionMode,
  isDocumentDisplay,
  isImageDisplay,
  isProductQuestion,
} from '../../../../../../services/Utils/ReportQuestions/report-questions.utils';

export const ReportAnswersComponent: ng.IComponentOptions = {
  bindings: {
    reportId: '<',
    question: '<',
    answers: '<',
    form: '<',
  },
  templateUrl:
    'reports/report/components/report-content/components/report-answers/report-answers.html',
  // eslint-disable-next-line max-params
  controller: class ReportAnswersController implements ng.IController {
    answers: Answer[] = [];
    form: Form;
    reportId: ObjectId;
    question: FormQuestion;
    formFilesPath: string;
    photoPathHash: Record<ObjectId, string> = {};
    documentAnswers: { _id: any; name: any; mime_type: any; size: any }[] = [];
    isProductQuestion = false;
    productQuestionMode: string;
    hasAnswers = false;

    constructor(
      private $q: IQService,
      private imageService: ImageService,
      private filesSystemService,
      private modalService: ModalService,
      private dateFormatService: DateFormatService,
      private dateService: DateService,
      private SF_IMAGE_SIZES,
      private imageSourceService: ImageSourceService,
      private formsService,
      private $translate: ng.translate.ITranslateService
    ) {
      'ngInject';
    }

    $onInit(): IPromise<void> {
      if (this.form) {
        this.formFilesPath = this.formsService.getFilesPath(this.form._id);
      }

      this.hasAnswers = (this.answers ?? []).length > 0;
      this.isProductQuestion = isProductQuestion(this.question);
      this.productQuestionMode = getProductQuestionMode(this.question);
      this.documentAnswers = this.getDocumentAnswers();

      return this.getPhotoPathHash();
    }

    getPicture = (answer: Answer) => {
      const pictureId = this.getValue(answer, 0);

      return answer.temp
        ? this.$q.when(
            this.filesSystemService.computeFileDataPath(
              this.reportId,
              pictureId
            )
          )
        : this.imageService.getSizedUrlFromId(
            pictureId,
            this.SF_IMAGE_SIZES.RECTANGLE_MEDIUM
          );
    };

    getValue(answer: Answer, valueIndex: number, naAllowed = false) {
      if (naAllowed) {
        return answer.values[valueIndex || 0].value ||
          answer.values[valueIndex || 0].value === 0
          ? answer.values[valueIndex || 0].value
          : this.$translate.instant('ABBR_NOT_APPLICABLE');
      }
      return answer.values[valueIndex || 0].value;
    }

    getTimeValue(answer: Answer) {
      return this.dateService.convertToUTCDate(answer.values[0].value);
    }

    getUnit(answer: Answer) {
      const unitField = this.question.fields
        .slice(1)
        .filter((field) => field.type !== 'score')[0];
      const unitValue = unitField
        ? answer.values.filter((value) => unitField._id === value.field_id)[0]
        : null;

      return unitValue ? unitValue.value : null;
    }

    getImage(answer: Answer) {
      return this.photoPathHash[this.getPictureId(answer)];
    }

    getDocument(answer: Answer) {
      return {
        _id: answer.values[0].value,
        name: answer.values[1].value,
        mime_type: answer.values[2].value,
        size: answer.values[3].value,
      };
    }

    viewPicture(answer: Answer) {
      const template = `
        <sf-images-viewer
          images="[$ctrl.image]"
          on-close="$ctrl.onClose()">
        </sf-images-viewer>
      `;
      const bindings = {
        image: this.imageSourceService.create(
          {
            url: this.getImage(answer),
            id: this.getPictureId(answer),
          },
          false
        ),
      };

      return this.modalService.open(template, bindings);
    }

    getPictureId(answer: Answer) {
      return answer.values[0].value;
    }

    isImageDisplayAnswer(answer: Answer) {
      return isImageDisplay(answer);
    }

    isDocumentDisplayAnswer(answer: Answer) {
      return isDocumentDisplay(answer);
    }

    getDateFormatted(date: string) {
      return this.dateFormatService.getDateFormatted(date);
    }

    getDocumentAnswers() {
      return (this.answers ?? [])
        .filter((answer) => this.isDocumentDisplayAnswer(answer))
        .map((answer) => this.getDocument(answer));
    }

    getPictureAnswers(): Answer[] {
      return (this.answers ?? []).filter((answer) =>
        this.isImageDisplayAnswer(answer)
      );
    }

    getPhotoPathHash(): IPromise<void> {
      const pictureAnswers = this.getPictureAnswers();

      return this.$q
        .all(
          pictureAnswers.map((pictureAnswer) =>
            this.getPicture(pictureAnswer).then((path) => {
              const pictureId = this.getValue(pictureAnswer, 0);

              this.photoPathHash[pictureId] = path;
            })
          )
        )
        .then();
    }
  },
};
